import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMountEffect } from '../../hooks/useMountEffect'
import { Popover, PopoverContent, PopoverTrigger } from '../UI/popover'

interface TimePickerProps {
  value?: string
  onChange: (value: string) => void
  className?: string
  disabled?: boolean
  /**
   * If true, the picker commits its displayed default ("09:00 AM") up to
   * the parent on mount when `value` is empty/malformed. Use this when the
   * picker is rendered into a transient form (modals, wizards) and you want
   * the visible value to match the parent's state immediately.
   *
   * Leave false (the default) for forms backed by async-loading data:
   * mounting before the data arrives would silently overwrite the loaded
   * value with the placeholder default.
   */
  commitDefaultOnMount?: boolean
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function parse24hTo12h(value?: string): {
  hour12: number
  minute: number
  period: 'AM' | 'PM'
} {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) {
    return { hour12: 9, minute: 0, period: 'AM' }
  }
  const [hStr, mStr] = value.split(':')
  let hour = parseInt(hStr, 10)
  const minute = parseInt(mStr, 10)
  const period: 'AM' | 'PM' = hour >= 12 ? 'PM' : 'AM'
  hour = hour % 12
  if (hour === 0) hour = 12
  return { hour12: hour, minute, period }
}

function to24h(hour12: number, minute: number, period: 'AM' | 'PM'): string {
  let hour = hour12 % 12
  if (period === 'PM') {
    hour += 12
  }
  return `${pad2(hour)}:${pad2(minute)}`
}

export default function TimePicker({
  value,
  onChange,
  className,
  disabled,
  commitDefaultOnMount = false,
}: TimePickerProps) {
  const {
    hour12: initialHour,
    minute: initialMinute,
    period: initialPeriod,
  } = useMemo(() => parse24hTo12h(value), [value])

  const [open, setOpen] = useState(false)
  const [hour12, setHour12] = useState<number>(initialHour)
  const [minute, setMinute] = useState<number>(initialMinute)
  const [period, setPeriod] = useState<'AM' | 'PM'>(initialPeriod)

  // Sync internal state if external value changes
  useEffect(() => {
    const parsed = parse24hTo12h(value)
    setHour12(parsed.hour12)
    setMinute(parsed.minute)
    setPeriod(parsed.period)
  }, [value])

  // Opt-in: when the parent provides no initial value, commit the displayed
  // default ("09:00 AM") up to the parent on mount. Without this the field
  // LOOKS picked (it shows 09:00 AM) but the parent's `value` stays empty,
  // which silently blocks downstream "is the form valid?" checks.
  //
  // Off by default because async-loaded forms can mount with an empty value
  // and then receive the real value moments later; committing the default
  // would race the loaded value and silently overwrite it. Disabled fields
  // are also exempt -- don't dirty a form just by rendering it disabled.
  useMountEffect(() => {
    if (!commitDefaultOnMount) return
    if (disabled) return
    if (!value || !/^\d{2}:\d{2}$/.test(value)) {
      onChange(to24h(initialHour, initialMinute, initialPeriod))
    }
  })

  const display = useMemo(() => {
    return `${pad2(hour12)}:${pad2(minute)} ${period}`
  }, [hour12, minute, period])

  const commitChange = useCallback(
    (h: number, m: number, p: 'AM' | 'PM') => {
      onChange(to24h(h, m, p))
    },
    [onChange]
  )

  const hours = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), [])
  const minutes = useMemo(() => Array.from({ length: 60 }, (_, i) => i), [])

  const handleFreeformChange = useCallback(
    (text: string) => {
      const lower = text.toLowerCase()
      const periodMatch = lower.match(/(am|pm)/)
      const nextPeriod: 'AM' | 'PM' = periodMatch
        ? (periodMatch[1].toUpperCase() as 'AM' | 'PM')
        : period

      // Try colon format first (e.g., 9:05 am)
      const colon = lower.match(
        /^(\s*)(\d{1,2})\s*:\s*(\d{1,2})(?:\s*(am|pm))?\s*$/
      )
      if (colon) {
        let h = parseInt(colon[2], 10)
        let m = parseInt(colon[3], 10)
        if (isNaN(h)) h = hour12
        if (isNaN(m)) m = minute
        if (m > 59) m = 59
        if (h <= 0) h = 12
        if (h > 12) h = ((h - 1) % 12) + 1
        const colonPeriod: 'AM' | 'PM' = colon[4]
          ? (colon[4].toUpperCase() as 'AM' | 'PM')
          : nextPeriod
        setHour12(h)
        setMinute(m)
        setPeriod(colonPeriod)
        commitChange(h, m, colonPeriod)
        return
      }

      // Remove non-digits for compact formats like "231" => 2:31
      const digits = lower.replace(/[^0-9]/g, '')
      if (digits.length === 0) return
      let h = hour12
      let m = minute
      if (digits.length === 1) {
        h = parseInt(digits.slice(0, 1), 10)
        m = 0
      } else if (digits.length === 2) {
        h = parseInt(digits.slice(0, 2), 10)
        m = 0
      } else if (digits.length === 3) {
        h = parseInt(digits.slice(0, 1), 10)
        m = parseInt(digits.slice(1), 10)
      } else {
        h = parseInt(digits.slice(0, 2), 10)
        m = parseInt(digits.slice(2, 4), 10)
      }
      if (isNaN(h)) h = hour12
      if (isNaN(m)) m = minute
      if (m > 59) m = 59
      if (h <= 0) h = 12
      if (h > 12) h = ((h - 1) % 12) + 1
      setHour12(h)
      setMinute(m)
      setPeriod(nextPeriod)
      commitChange(h, m, nextPeriod)
    },
    [commitChange, hour12, minute, period]
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* className lands on this wrapper -- consumers pass layout classes
          ("w-full", "flex-1") that expect a block container. The input
          itself gets the field styling. */}
      <div className={`relative ${className ?? ''}`}>
        <PopoverTrigger asChild>
          {/* No onClick here: PopoverTrigger injects its own click handler
              via asChild. Adding our own would race Radix in the same
              render batch and the two toggles would cancel out -- the
              picker would fail to reopen after the first close. */}
          <input
            type="text"
            className="form-control w-full text-left"
            value={display}
            onChange={(e) => handleFreeformChange(e.target.value)}
            onFocus={() => !disabled && setOpen(true)}
            placeholder="hh:mm AM"
            disabled={disabled}
            aria-label="Time"
          />
        </PopoverTrigger>
      </div>
      {/* Popover portals to document.body so the panel escapes the modal's
          overflow-y-auto clipping. Keep onOpenAutoFocus suppressed so the
          input stays focused for keyboard input. */}
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div
          className="grid gap-2 p-2"
          style={{ gridTemplateColumns: '72px 72px 64px' }}
        >
          <div className="max-h-64 overflow-y-auto pr-2 w-[72px] text-center">
            {hours.map((h) => (
              <div
                key={`h-${h}`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setHour12(h)
                  commitChange(h, minute, period)
                }}
                className={`px-2 py-2 rounded cursor-pointer text-center ${
                  h === hour12
                    ? 'bg-fb-dark-green text-white'
                    : 'hover:bg-gray-100'
                }`}
              >
                {pad2(h)}
              </div>
            ))}
          </div>

          <div className="max-h-64 overflow-y-auto pr-2 w-[72px] text-center">
            {minutes.map((m) => (
              <div
                key={`m-${m}`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setMinute(m)
                  commitChange(hour12, m, period)
                }}
                className={`px-2 py-2 rounded cursor-pointer text-center ${
                  m === minute
                    ? 'bg-fb-dark-green text-white'
                    : 'hover:bg-gray-100'
                }`}
              >
                {pad2(m)}
              </div>
            ))}
          </div>

          <div className="max-h-64 overflow-y-auto w-16 text-center">
            {(['AM', 'PM'] as const).map((p) => (
              <div
                key={`p-${p}`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setPeriod(p)
                  commitChange(hour12, minute, p)
                }}
                className={`px-2 py-2 rounded cursor-pointer text-center ${
                  p === period
                    ? 'bg-fb-dark-green text-white'
                    : 'hover:bg-gray-100'
                }`}
              >
                {p}
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
